import {
  PRESET_INTERCHANGE_FORMAT,
  WORKSPACE_DOCUMENT_VERSION,
  cloneValue,
  collectionKey,
  contractFor,
  decodePayload,
  defaultClock,
  defaultCreateId,
  detachedSource,
  exactFields,
  fixedRows,
  frozenClone,
  isRecord,
  isoTimestamp,
  opaqueId,
  payloadsEqual,
  providerFailure,
  runtimeFixedPresetDescriptors,
  savedPresetName,
  timestampRange,
} from "./preset-internals.js";
import { decodePresetInterchange } from "./preset-interchange.js";
import { createPresetRuntimeBindings } from "./preset-runtime-bindings.js";
import { createLocalPresetSourceController } from "./local-preset-sources.js";
import {
  activeWorkspaceChangeNodes,
  canonicalWorkspaceActionKind,
  compactWorkspaceHistory,
  decodeWorkspaceDocument,
  encodeWorkspaceDocument,
  projectWorkspaceHistory,
  workspaceChildRows,
  workspaceRevisionMap,
} from "./workspace-history.js";

export { PRESET_INTERCHANGE_FORMAT } from "./preset-internals.js";

const SOURCE_INPUT_FIELDS = Object.freeze(["id", "kind"]);
const FIXED_PRESET_DEFINITION_FIELDS = Object.freeze(["id", "name", "payload"]);
const INITIAL_EDIT_FIELDS = Object.freeze(["actionId", "actionKind"]);

function loading(key) {
  return frozenClone({ kind: "loading", collectionKey: key });
}

function uninitialized(key) {
  return frozenClone({ kind: "uninitialized", collectionKey: key });
}

function canonicalActionId(value, createId) {
  return value === undefined
    ? opaqueId(createId("preset_action"), "Preset action ID")
    : opaqueId(value, "Preset action ID");
}

function canonicalSourceInput(value) {
  if (value === undefined) {
    return { kind: "none", id: "" };
  }
  if (!exactFields(value, SOURCE_INPUT_FIELDS)) {
    throw new TypeError("Preset source must contain exactly kind and id.");
  }
  if (value.kind === "none") {
    if (value.id !== "") {
      throw new TypeError("Detached preset source ID must be empty.");
    }
    return { kind: "none", id: "" };
  }
  if (value.kind !== "fixed" && value.kind !== "local" && value.kind !== "preset") {
    throw new TypeError("Preset source kind must be none, fixed, local, or preset.");
  }
  return { kind: value.kind, id: opaqueId(value.id, "Preset source ID") };
}

function scopedSourceProjection(source, savedState, localPresetSourceFields) {
  if (!source || (source.kind !== "preset" && source.kind !== "local")) {
    return { source, liveSource: null, authorityKnown: true };
  }
  const savedAuthorityKnown = savedState.kind === "ready" || savedState.kind === "missing";
  const savedPreset = savedState.kind === "ready"
    ? savedState.presets.find((preset) => preset.id === source.id) ?? null
    : null;
  const localSourceKind = localPresetSourceFields.localPresetSources?.kind ?? "hidden";
  const localAuthorityKnown = ["hidden", "missing", "ready"].includes(localSourceKind);
  const localPreset = localSourceKind === "ready"
    ? localPresetSourceFields.localPresets.find((preset) => preset.id === source.id) ?? null
    : null;

  if (source.kind === "preset") {
    if (savedPreset) {
      return { source, liveSource: savedPreset, authorityKnown: true };
    }
    if (localPreset) {
      return {
        source: { ...source, kind: "local" },
        liveSource: localPreset,
        authorityKnown: true,
      };
    }
    return {
      source,
      liveSource: null,
      authorityKnown: savedAuthorityKnown && localAuthorityKnown,
    };
  }

  if (localPreset) {
    return { source, liveSource: localPreset, authorityKnown: true };
  }
  if (localSourceKind === "hidden" && savedPreset) {
    return {
      source: { ...source, kind: "preset" },
      liveSource: savedPreset,
      authorityKnown: true,
    };
  }
  return {
    source,
    liveSource: null,
    authorityKnown: localAuthorityKnown
      && (localSourceKind !== "hidden" || savedAuthorityKnown),
  };
}

function combinedRevision(workspaceState, savedState) {
  const workspaceRevision = workspaceState.kind === "ready"
    ? workspaceState.storeRevision
    : workspaceState.kind;
  const savedRevision = savedState.kind === "ready" ? savedState.revision : savedState.kind;
  return `${workspaceRevision}|${savedRevision}`;
}

function savedReferenceProjection(savedState) {
  if (savedState.kind === "ready") {
    return {
      kind: "ready",
      revision: savedState.revision,
    };
  }
  if (savedState.kind === "missing") {
    return { kind: "missing" };
  }
  return {
    kind: savedState.kind,
    ...(savedState.operation === undefined ? {} : { operation: savedState.operation }),
    ...(savedState.reason === undefined ? {} : { reason: savedState.reason }),
  };
}

function savedReferenceFields(savedState) {
  return {
    savedReferences: savedReferenceProjection(savedState),
    presets: savedState.kind === "ready"
      ? savedState.presets.map((preset) => ({
          ...cloneValue(preset),
          kind: "preset",
        }))
      : savedState.kind === "missing" ? [] : null,
  };
}

function readyProjection(
  workspace,
  savedState,
  localPresetSourceFields,
  contract,
  fixedPresetRows,
  deferredFixedPresetIds,
  quarantineCount = 0,
) {
  for (const revision of workspace.revisions) {
    revision.basedOn = scopedSourceProjection(
      revision.basedOn,
      savedState,
      localPresetSourceFields,
    ).source;
  }
  const byId = workspaceRevisionMap(workspace);
  const head = byId.get(workspace.headRevisionId);
  const scopedSource = scopedSourceProjection(
    head.basedOn,
    savedState,
    localPresetSourceFields,
  );
  let basedOn = cloneValue(scopedSource.source);
  let base = basedOn.kind === "none"
    ? null
    : byId.get(basedOn.revisionId) || null;
  if (base && basedOn.kind !== "none") {
    const fixedSource = basedOn.kind === "fixed";
    const liveSource = fixedSource
      ? fixedPresetRows.find((preset) => preset.id === basedOn.id) || null
      : scopedSource.liveSource;
    const sourceChanged = liveSource
      && !payloadsEqual(contract, base.payload, liveSource.payload);
    const deferredFixedSource = fixedSource
      && !liveSource
      && deferredFixedPresetIds.has(basedOn.id);
    const sourceAuthorityKnown = fixedSource || scopedSource.authorityKnown;
    if (
      (sourceAuthorityKnown && !liveSource && !deferredFixedSource)
      || sourceChanged
    ) {
      basedOn = detachedSource();
      base = null;
    }
  }
  const modified = basedOn.kind === "none"
    || !base
    || !payloadsEqual(contract, head.payload, base.payload);
  return frozenClone({
    kind: "ready",
    collectionKey: workspace.collectionKey,
    revision: combinedRevision(workspace, savedState),
    current: {
      changeId: workspace.headChangeId,
      revisionId: workspace.headRevisionId,
      payload: cloneValue(head.payload),
      basedOn,
      modified,
      createdAt: workspace.createdAt,
      updatedAt: workspace.updatedAt,
    },
    ...savedReferenceFields(savedState),
    ...localPresetSourceFields,
    history: projectWorkspaceHistory(workspace, contract),
    quarantineCount,
  });
}

function nonReadyProjection(key, workspaceState, savedState, localPresetSourceFields) {
  if (workspaceState.kind === "invalid" || workspaceState.kind === "unsupportedVersion") {
    return frozenClone({ ...workspaceState, collectionKey: key });
  }
  if (workspaceState.kind === "unavailable") {
    return frozenClone({ ...workspaceState, collectionKey: key });
  }
  if (workspaceState.kind === "missing") {
    return frozenClone({
      kind: "missing",
      collectionKey: key,
      revision: combinedRevision(workspaceState, savedState),
      ...savedReferenceFields(savedState),
      ...localPresetSourceFields,
    });
  }
  return frozenClone({ kind: "unavailable", collectionKey: key, reason: "state-composition-failed" });
}

function exactOptions(value, allowed, label) {
  if (value === undefined) {
    return {};
  }
  if (!isRecord(value) || Object.keys(value).some((field) => !allowed.includes(field))) {
    throw new TypeError(`${label} contains unsupported fields.`);
  }
  return value;
}

function rawRecordsEqual(left, right) {
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch (_error) {
    return false;
  }
}

export function createPresetWorkspaceRepository({
  provider,
  savedPresets,
  collectionContracts,
  broadcaster = null,
  eventTarget = null,
  clock = defaultClock,
  createId = defaultCreateId,
} = {}) {
  if (!provider || typeof provider.readWorkspace !== "function" || typeof provider.mutateWorkspace !== "function") {
    throw new TypeError("Preset workspace requires a changeset provider.");
  }
  if (!savedPresets || typeof savedPresets.load !== "function") {
    throw new TypeError("Preset workspace requires a saved-preset repository.");
  }
  if (typeof clock !== "function" || typeof createId !== "function") {
    throw new TypeError("Preset workspace clock and ID factory must be functions.");
  }

  const states = new Map();
  const listeners = new Map();
  const runtimeBindings = createPresetRuntimeBindings();
  const localPresetSources = createLocalPresetSourceController({ repository: savedPresets });
  const definedFixedPresets = new Map();
  const projections = new Map();
  const savedLoads = new Map();
  const savedLoadGenerations = new Map();
  const savedStates = new Map();
  let disposed = false;

  function keys() {
    return collectionContracts instanceof Map
      ? Array.from(collectionContracts.keys()).map(collectionKey)
      : Object.keys(collectionContracts || {}).map(collectionKey);
  }

  function fixedPresetRowsFor(key, contract = contractFor(collectionContracts, key)) {
    const rows = new Map(fixedRows(contract).map((row) => [row.id, row]));
    for (const row of definedFixedPresets.get(key)?.values() || []) {
      rows.set(row.id, row);
    }
    return Array.from(rows.values());
  }

  function id(prefix) {
    return opaqueId(createId(prefix), `${prefix} ID`);
  }

  function timestamp() {
    return isoTimestamp(clock(), "Preset workspace time");
  }

  function state(rawKey) {
    const key = collectionKey(rawKey);
    return states.has(key) ? frozenClone(states.get(key)) : uninitialized(key);
  }

  function deliver(key, listener) {
    try {
      listener(state(key));
    } catch (error) {
      globalThis.reportError?.(error);
    }
  }

  function publish(key, next) {
    const frozen = frozenClone(next);
    states.set(key, frozen);
    if (!disposed) {
      for (const listener of listeners.get(key) || []) {
        deliver(key, listener);
      }
    }
    return state(key);
  }

  function subscribe(rawKey, listener) {
    const key = collectionKey(rawKey);
    contractFor(collectionContracts, key);
    if (typeof listener !== "function") {
      throw new TypeError("Preset workspace listener must be a function.");
    }
    if (disposed) {
      return () => {};
    }
    if (!listeners.has(key)) {
      listeners.set(key, new Set());
    }
    listeners.get(key).add(listener);
    deliver(key, listener);
    return () => listeners.get(key)?.delete(listener);
  }

  function currentSavedState(key) {
    return savedStates.get(key) ?? frozenClone({
      kind: "uninitialized",
      collectionKey: key,
    });
  }

  function rememberSavedState(key, value) {
    const next = frozenClone(value);
    savedStates.set(key, next);
    return next;
  }

  function savedLoadGeneration(key) {
    return savedLoadGenerations.get(key) ?? 0;
  }

  function obsoleteSavedLoad(key) {
    savedLoadGenerations.set(key, savedLoadGeneration(key) + 1);
    savedLoads.delete(key);
    localPresetSources.invalidate(key);
  }

  function savedLoading(key) {
    return frozenClone({ kind: "loading", collectionKey: key });
  }

  function hideSavedReferences(key) {
    const pending = rememberSavedState(key, savedLoading(key));
    const current = states.get(key);
    if (!current || !["ready", "missing"].includes(current.kind)) {
      if (!current || ["loading", "uninitialized"].includes(current.kind)) {
        publish(key, loading(key));
      }
      return state(key);
    }
    const workspaceRevision = current.revision.split("|", 1)[0];
    return publish(key, {
      ...current,
      revision: `${workspaceRevision}|loading`,
      ...savedReferenceFields(pending),
      ...localPresetSources.projectionFields(key),
    });
  }

  async function loadSavedState(key) {
    const generation = savedLoadGeneration(key);
    const existing = savedLoads.get(key);
    if (existing?.generation === generation) return existing.promise;

    const pending = (async () => {
      const localLoad = localPresetSources.load(key);
      void localLoad.then(() => {
        if (!disposed && generation === savedLoadGeneration(key)) {
          void requestProjection(key, "external");
        }
      });
      try {
        const next = await savedPresets.load(key);
        if (disposed || generation !== savedLoadGeneration(key)) {
          return currentSavedState(key);
        }
        return rememberSavedState(key, next);
      } catch (_error) {
        const failure = {
          kind: "unavailable",
          collectionKey: key,
          operation: "load-saved-presets",
          reason: "saved-presets-load-failed",
        };
        if (disposed || generation !== savedLoadGeneration(key)) {
          return currentSavedState(key);
        }
        return rememberSavedState(key, failure);
      }
    })().finally(() => {
      if (savedLoads.get(key)?.promise === pending) savedLoads.delete(key);
    });
    savedLoads.set(key, { generation, promise: pending });
    return pending;
  }

  async function readProjection(key) {
    const contract = contractFor(collectionContracts, key);
    try {
      const [rawWorkspace, quarantine] = await Promise.all([
        provider.readWorkspace(key),
        typeof provider.quarantineForCollection === "function"
          ? provider.quarantineForCollection(key)
          : Promise.resolve([]),
      ]);
      const savedState = currentSavedState(key);
      const localPresetSourceFields = localPresetSources.projectionFields(key);
      const workspace = decodeWorkspaceDocument(rawWorkspace, contract, key);
      const deferredFixedPresetIds = new Set(
        runtimeFixedPresetDescriptors(contract).map((descriptor) => descriptor.id),
      );
      return workspace.kind === "ready"
        ? readyProjection(
            workspace,
            savedState,
            localPresetSourceFields,
            contract,
            fixedPresetRowsFor(key, contract),
            deferredFixedPresetIds,
            quarantine.length,
          )
        : nonReadyProjection(key, workspace, savedState, localPresetSourceFields);
    } catch (error) {
      return frozenClone({ ...providerFailure("read-preset-workspace", error), collectionKey: key });
    }
  }

  function projectionController(key) {
    if (!projections.has(key)) {
      projections.set(key, {
        cause: "external",
        completed: 0,
        requested: 0,
        running: null,
        waiters: [],
      });
    }
    return projections.get(key);
  }

  function settleProjectionWaiters(controller, value) {
    const remaining = [];
    for (const waiter of controller.waiters) {
      if (waiter.generation <= controller.completed || disposed) {
        waiter.resolve(value);
      } else {
        remaining.push(waiter);
      }
    }
    controller.waiters = remaining;
  }

  async function runProjectionLoop(key, controller) {
    let projected = state(key);
    try {
      while (!disposed && controller.completed < controller.requested) {
        const generation = controller.requested;
        const cause = controller.cause;
        const next = await readProjection(key);
        if (disposed || generation !== controller.requested) continue;
        let applied = next;
        try {
          await runtimeBindings.apply(key, next, cause);
        } catch (_error) {
          applied = {
            kind: "unavailable",
            collectionKey: key,
            operation: "apply-current",
            reason: "runtime-apply-failed",
            authoritative: next,
          };
        }
        if (disposed || generation !== controller.requested) continue;
        projected = publish(key, applied);
        controller.completed = generation;
        settleProjectionWaiters(controller, projected);
      }
      if (disposed) settleProjectionWaiters(controller, state(key));
      return projected;
    } finally {
      controller.running = null;
    }
  }

  function requestProjection(key, cause) {
    if (disposed) return Promise.resolve(state(key));
    const controller = projectionController(key);
    controller.requested += 1;
    controller.cause = cause;
    const generation = controller.requested;
    const result = new Promise((resolve) => {
      controller.waiters.push({ generation, resolve });
    });
    if (!controller.running) {
      controller.running = runProjectionLoop(key, controller);
    }
    return result;
  }

  async function refresh(rawKey, cause = "external", { refreshSaved = false } = {}) {
    const key = collectionKey(rawKey);
    if (refreshSaved) {
      await loadSavedState(key);
      if (disposed) return state(key);
    }
    return requestProjection(key, cause);
  }

  function reprojectLocally(key) {
    return requestProjection(key, "external");
  }

  function beginLoad(key) {
    if (states.get(key)?.kind === "ready") {
      hideSavedReferences(key);
    } else {
      rememberSavedState(key, savedLoading(key));
      publish(key, loading(key));
    }
  }

  async function load(rawKey = undefined) {
    if (rawKey !== undefined) {
      const key = collectionKey(rawKey);
      beginLoad(key);
      const saved = loadSavedState(key);
      const current = await requestProjection(key, "load");
      void saved.then(() => requestProjection(key, "external"));
      return current;
    }
    const collectionKeys = keys();
    for (const key of collectionKeys) {
      beginLoad(key);
    }
    const savedLoads = collectionKeys.map((key) => loadSavedState(key));
    const loaded = await Promise.all(collectionKeys.map((key) => (
      requestProjection(key, "load")
    )));
    for (let index = 0; index < collectionKeys.length; index += 1) {
      const key = collectionKeys[index];
      void savedLoads[index].then(() => requestProjection(key, "external"));
    }
    return frozenClone(Object.fromEntries(loaded.map((value) => [value.collectionKey, value])));
  }

  function broadcast(key, revision, boundary = "workspace") {
    try {
      broadcaster?.postMessage?.({ boundary, collectionKey: key, storeRevision: revision });
    } catch (error) {
      globalThis.reportError?.(error);
    }
  }

  function checkCurrentExpected(current, expectedRevision) {
    if (expectedRevision === undefined) {
      return null;
    }
    const expected = opaqueId(expectedRevision, "Expected workspace revision");
    const expectedWorkspaceRevision = expected.split("|", 1)[0];
    const actualWorkspaceRevision = current.revision?.split("|", 1)[0];
    if (actualWorkspaceRevision === expectedWorkspaceRevision) {
      return null;
    }
    return frozenClone({
      kind: "conflict",
      collectionKey: current.collectionKey,
      expectedRevision: expected,
      actualRevision: current.revision,
      authoritative: current,
    });
  }

  async function currentReady(key, expectedRevision) {
    const current = await refresh(key, "external");
    const expectedConflict = checkCurrentExpected(current, expectedRevision);
    if (expectedConflict) {
      return { state: expectedConflict, ready: null };
    }
    return { state: current, ready: current.kind === "ready" ? current : null };
  }

  async function savedMutationFailure(key, result, expectedRevision) {
    if (result.kind === "conflict" && result.authoritative) {
      rememberSavedState(key, result.authoritative);
    } else if (result.reason === "account-authority-changed") {
      await loadSavedState(key);
    } else if (result.kind !== "conflict") {
      rememberSavedState(key, result);
    } else {
      await loadSavedState(key);
    }
    const authoritative = await refresh(key, "external");
    return frozenClone({
      ...result,
      collectionKey: key,
      boundary: "saved-references",
      expectedRevision,
      actualRevision: authoritative.revision ?? null,
      authoritative,
    });
  }

  function changedSavedAuthority(key, generation, operation, expectedRevision) {
    if (generation === savedLoadGeneration(key)) {
      return null;
    }
    return savedMutationFailure(key, {
      kind: "unavailable",
      collectionKey: key,
      operation,
      reason: "account-authority-changed",
    }, expectedRevision);
  }

  function savedAuthorityMatches(key, generation) {
    return generation === undefined || generation === savedLoadGeneration(key);
  }

  function savedAuthorityBoundaryFailure(key, operation, expectedRevision) {
    const authoritative = state(key);
    return frozenClone({
      kind: "unavailable",
      collectionKey: key,
      boundary: "saved-references",
      operation,
      reason: "account-authority-changed",
      expectedRevision: expectedRevision ?? null,
      actualRevision: authoritative.revision ?? null,
      authoritative,
    });
  }

  function savedReferenceFailure(key, operation, authoritative) {
    const savedState = currentSavedState(key);
    return frozenClone({
      kind: savedState.kind,
      collectionKey: key,
      boundary: "saved-references",
      operation,
      reason: savedState.reason ?? "saved-references-not-ready",
      authoritative,
    });
  }

  function savedReferencesReady(key) {
    return ["missing", "ready"].includes(currentSavedState(key).kind);
  }

  async function mutateWorkspace(key, expectedStoreRevision, reducer, cause, effect = null) {
    const contract = contractFor(collectionContracts, key);
    const published = states.get(key);
    const expectedPublicRevision = published?.kind === "ready"
      && published.revision.split("|")[0] === expectedStoreRevision
      ? published.revision
      : expectedStoreRevision;
    let mutationResult;
    try {
      mutationResult = await provider.mutateWorkspace(key, (raw) => {
        const decoded = decodeWorkspaceDocument(raw, contract, key);
        if (decoded.kind !== "ready") {
          return { record: raw, result: decoded };
        }
        if (expectedStoreRevision !== undefined && decoded.storeRevision !== expectedStoreRevision) {
          return {
            record: raw,
            result: {
              kind: "conflict",
              collectionKey: key,
              expectedRevision: expectedStoreRevision,
              actualRevision: decoded.storeRevision,
            },
          };
        }
        const working = cloneValue(decoded);
        const reduced = reducer(working, contract);
        if (
          reduced?.kind === "conflict"
          || (
            reduced?.kind === "unavailable"
            && reduced.boundary === "saved-references"
          )
        ) {
          return { record: raw, result: reduced };
        }
        reduced.storeRevision = id("workspace_store_revision");
        const updatedAt = timestamp();
        timestampRange(reduced.createdAt, updatedAt, "Workspace");
        reduced.updatedAt = updatedAt;
        return { record: encodeWorkspaceDocument(reduced), result: { kind: "written" } };
      });
    } catch (error) {
      return publish(key, { ...providerFailure("write-preset-workspace", error), collectionKey: key });
    }
    if (mutationResult.kind !== "written") {
      if (mutationResult.kind === "conflict") {
        const authoritative = await refresh(key, "external");
        return frozenClone({
          ...mutationResult,
          expectedRevision: expectedPublicRevision,
          actualRevision: authoritative.revision ?? null,
          authoritative,
        });
      }
      return frozenClone(mutationResult);
    }
    const next = await refresh(key, cause);
    if (next.kind === "ready") {
      broadcast(key, next.revision);
      return effect ? frozenClone({ ...next, effect: effect(next) }) : next;
    }
    return next;
  }

  function makeRoot(key, contract, payload, source, initialEdit = null) {
    const at = timestamp();
    const revisionId = id("workspace_revision");
    const changeId = id("workspace_change");
    const revision = {
      revisionId,
      changeId,
      parentRevisionId: null,
      supersedesRevisionId: null,
      actionId: id("preset_action"),
      actionKind: "base",
      at,
      basedOn: source.kind === "none"
        ? detachedSource()
        : { kind: source.kind, id: source.id, revisionId },
      compacted: false,
      payload: decodePayload(contract, payload),
    };
    const workspace = {
      kind: "ready",
      documentVersion: WORKSPACE_DOCUMENT_VERSION,
      collectionKey: key,
      storeRevision: id("workspace_store_revision"),
      headRevisionId: revisionId,
      headChangeId: changeId,
      revisions: [revision],
      lastVisitedChildren: [],
      createdAt: at,
      updatedAt: at,
    };
    if (initialEdit) {
      const currentPayload = decodePayload(contract, initialEdit.payload);
      if (!payloadsEqual(contract, revision.payload, currentPayload)) {
        const currentRevisionId = id("workspace_revision");
        const currentAt = timestamp();
        workspace.revisions.push({
          revisionId: currentRevisionId,
          changeId,
          parentRevisionId: revisionId,
          supersedesRevisionId: null,
          actionId: initialEdit.actionId,
          actionKind: initialEdit.actionKind,
          at: currentAt,
          basedOn: cloneValue(revision.basedOn),
          compacted: false,
          payload: currentPayload,
        });
        workspace.headRevisionId = currentRevisionId;
        workspace.lastVisitedChildren.push({
          parentRevisionId: revisionId,
          childRevisionId: currentRevisionId,
        });
        workspace.updatedAt = currentAt;
      }
    }
    return workspace;
  }

  async function resolveSource(key, source, suppliedPayload) {
    const contract = contractFor(collectionContracts, key);
    if (source.kind === "none") {
      return { source, payload: decodePayload(contract, suppliedPayload) };
    }
    if (source.kind === "fixed") {
      const fixed = fixedPresetRowsFor(key, contract).find((row) => row.id === source.id);
      if (!fixed) {
        throw new TypeError(`Unknown fixed preset ${source.id}.`);
      }
      return { source, payload: fixed.payload };
    }
    if (source.kind === "local") {
      const resolved = await localPresetSources.resolve(key, source.id);
      if (resolved.kind !== "ready") {
        throw new TypeError("Browser-local presets are not ready.");
      }
      return { source, payload: resolved.preset.payload };
    }
    const saved = await loadSavedState(key);
    if (saved.kind !== "ready") {
      throw new TypeError("Saved presets are not ready.");
    }
    const preset = saved.presets.find((row) => row.id === source.id);
    if (!preset) {
      throw new TypeError(`Unknown saved preset ${source.id}.`);
    }
    return { source, payload: preset.payload };
  }

  async function initializeWorkspace(
    rawKey,
    payload,
    options = {},
    expectedSavedAuthorityGeneration = undefined,
  ) {
    const key = collectionKey(rawKey);
    const parsed = exactOptions(
      options,
      ["expectedRevision", "initialEdit", "source"],
      "Initialize options",
    );
    const source = canonicalSourceInput(parsed.source);
    const savedAuthorityGeneration = source.kind === "preset" || source.kind === "local"
      ? expectedSavedAuthorityGeneration ?? savedLoadGeneration(key)
      : undefined;
    const current = await refresh(key, "external");
    const expectedConflict = checkCurrentExpected(current, parsed.expectedRevision);
    if (expectedConflict) {
      return expectedConflict;
    }
    if (current.kind === "ready") {
      return current;
    }
    if (current.kind !== "missing") {
      return current;
    }
    let resolved;
    try {
      resolved = await resolveSource(key, source, payload);
    } catch (error) {
      throw new TypeError(error.message);
    }
    if (!savedAuthorityMatches(key, savedAuthorityGeneration)) {
      return savedAuthorityBoundaryFailure(
        key,
        "initialize-from-saved-preset",
        parsed.expectedRevision ?? current.revision,
      );
    }
    const contract = contractFor(collectionContracts, key);
    let initialEdit = null;
    if (parsed.initialEdit !== undefined) {
      if (!exactFields(parsed.initialEdit, INITIAL_EDIT_FIELDS)) {
        throw new TypeError("Initial edit must contain exactly actionId and actionKind.");
      }
      initialEdit = {
        actionId: canonicalActionId(parsed.initialEdit.actionId, createId),
        actionKind: canonicalWorkspaceActionKind(parsed.initialEdit.actionKind),
        payload,
      };
    }
    let mutationResult;
    try {
      mutationResult = await provider.mutateWorkspace(key, (raw) => {
        if (!savedAuthorityMatches(key, savedAuthorityGeneration)) {
          return {
            record: raw,
            result: savedAuthorityBoundaryFailure(
              key,
              "initialize-from-saved-preset",
              parsed.expectedRevision ?? current.revision,
            ),
          };
        }
        if (raw !== null) {
          return { record: raw, result: { kind: "conflict" } };
        }
        const root = makeRoot(
          key,
          contract,
          resolved.payload,
          resolved.source,
          initialEdit,
        );
        return { record: encodeWorkspaceDocument(root), result: { kind: "written" } };
      });
    } catch (error) {
      return publish(key, { ...providerFailure("initialize-preset-workspace", error), collectionKey: key });
    }
    if (mutationResult.kind !== "written") {
      if (
        mutationResult.kind === "unavailable"
        && mutationResult.reason === "account-authority-changed"
      ) {
        return frozenClone(mutationResult);
      }
      const authoritative = await refresh(key, "external");
      return frozenClone({
        kind: "conflict",
        collectionKey: key,
        expectedRevision: parsed.expectedRevision ?? current.revision,
        actualRevision: authoritative.revision ?? null,
        authoritative,
      });
    }
    const next = await refresh(key, "load");
    if (next.kind === "ready") {
      broadcast(key, next.revision);
    }
    return next;
  }

  function initialize(rawKey, payload, options = {}) {
    return initializeWorkspace(rawKey, payload, options);
  }

  function setLastVisited(workspace, parentRevisionId, childRevisionId) {
    workspace.lastVisitedChildren = workspace.lastVisitedChildren
      .filter((entry) => entry.parentRevisionId !== parentRevisionId);
    workspace.lastVisitedChildren.push({ parentRevisionId, childRevisionId });
  }

  function commitCurrentPayload(key, current, canonicalPayload, {
    actionId: rawActionId,
    actionKind: rawActionKind,
  } = {}) {
    const actionId = canonicalActionId(rawActionId, createId);
    const actionKind = canonicalWorkspaceActionKind(rawActionKind);
    let moved = null;
    const expectedStoreRevision = current.revision.split("|")[0];
    return mutateWorkspace(key, expectedStoreRevision, (workspace) => {
      const byId = workspaceRevisionMap(workspace);
      const head = byId.get(workspace.headRevisionId);
      const headHasChildren = activeWorkspaceChangeNodes(workspace).some((revision) => (
        revision.parentRevisionId === head.revisionId
      ));
      const amend = head.actionKind !== "base"
        && head.actionId === actionId
        && head.basedOn.revisionId !== head.revisionId
        && !headHasChildren;
      const revisionId = id("workspace_revision");
      const revision = {
        revisionId,
        changeId: workspace.headChangeId,
        parentRevisionId: amend ? head.parentRevisionId : head.revisionId,
        supersedesRevisionId: null,
        actionId,
        actionKind,
        at: timestamp(),
        basedOn: cloneValue(head.basedOn),
        compacted: false,
        payload: canonicalPayload,
      };
      if (amend) {
        workspace.revisions = workspace.revisions.filter((candidate) => (
          candidate.revisionId !== head.revisionId
        ));
        workspace.lastVisitedChildren = workspace.lastVisitedChildren.filter((entry) => (
          entry.parentRevisionId !== head.revisionId
          && entry.childRevisionId !== head.revisionId
        ));
      }
      workspace.revisions.push(revision);
      workspace.headRevisionId = revisionId;
      if (revision.parentRevisionId) {
        setLastVisited(workspace, revision.parentRevisionId, revisionId);
      }
      moved = { kind: "head-moved", changeId: revision.changeId, revisionId, actionId };
      return compactWorkspaceHistory(workspace);
    }, "edit", () => moved);
  }

  async function edit(rawKey, payload, options = {}) {
    const key = collectionKey(rawKey);
    const parsed = exactOptions(
      options,
      ["actionId", "actionKind", "expectedRevision"],
      "Preset edit options",
    );
    let current = await refresh(key, "external");
    const expectedConflict = checkCurrentExpected(current, parsed.expectedRevision);
    if (expectedConflict) {
      return expectedConflict;
    }
    const contract = contractFor(collectionContracts, key);
    const canonicalPayload = decodePayload(contract, payload);
    if (current.kind === "missing") {
      return initialize(key, canonicalPayload, {
        source: { kind: "none", id: "" },
        expectedRevision: current.revision,
      });
    }
    if (current.kind !== "ready") {
      return current;
    }
    if (payloadsEqual(contract, current.current.payload, canonicalPayload)) {
      return current;
    }
    return commitCurrentPayload(key, current, canonicalPayload, {
      actionId: parsed.actionId,
      actionKind: parsed.actionKind,
    });
  }

  async function replaceCurrent(
    key,
    payload,
    source,
    cause,
    expectedRevision,
    savedAuthorityGeneration = undefined,
  ) {
    const current = await refresh(key, "external");
    const expectedConflict = checkCurrentExpected(current, expectedRevision);
    if (expectedConflict) {
      return expectedConflict;
    }
    if (!savedAuthorityMatches(key, savedAuthorityGeneration)) {
      return savedAuthorityBoundaryFailure(key, cause, expectedRevision);
    }
    if (current.kind === "missing") {
      return initializeWorkspace(
        key,
        payload,
        { source, expectedRevision: current.revision },
        savedAuthorityGeneration,
      );
    }
    if (current.kind !== "ready") {
      return current;
    }
    const contract = contractFor(collectionContracts, key);
    const canonicalPayload = decodePayload(contract, payload);
    const expectedStoreRevision = current.revision.split("|")[0];
    let moved = null;
    return mutateWorkspace(key, expectedStoreRevision, (workspace) => {
      if (!savedAuthorityMatches(key, savedAuthorityGeneration)) {
        return savedAuthorityBoundaryFailure(key, cause, expectedRevision);
      }
      const revisionId = id("workspace_revision");
      const revision = {
        revisionId,
        changeId: workspace.headChangeId,
        parentRevisionId: null,
        supersedesRevisionId: null,
        actionId: id("preset_action"),
        actionKind: "base",
        at: timestamp(),
        basedOn: source.kind === "none"
          ? detachedSource()
          : { kind: source.kind, id: source.id, revisionId },
        compacted: false,
        payload: canonicalPayload,
      };
      workspace.revisions.push(revision);
      workspace.headRevisionId = revisionId;
      moved = {
        kind: "current-replaced",
        changeId: workspace.headChangeId,
        revisionId,
      };
      return workspace;
    }, cause, () => moved);
  }

  async function usePreset(rawKey, presetId, options = {}) {
    const key = collectionKey(rawKey);
    const parsed = exactOptions(options, ["expectedRevision"], "Use preset options");
    const authorityGeneration = savedLoadGeneration(key);
    const saved = await loadSavedState(key);
    if (saved.kind !== "ready") {
      const authoritative = await refresh(key, "external");
      return savedReferenceFailure(key, "use-preset", authoritative);
    }
    const idValue = opaqueId(presetId, "Saved preset ID");
    const preset = saved.presets.find((row) => row.id === idValue);
    if (!preset) {
      throw new TypeError(`Unknown saved preset ${idValue}.`);
    }
    return replaceCurrent(
      key,
      preset.payload,
      { kind: "preset", id: preset.id },
      "use-preset",
      parsed.expectedRevision,
      authorityGeneration,
    );
  }

  async function useLocalPreset(rawKey, presetId, options = {}) {
    const key = collectionKey(rawKey);
    const parsed = exactOptions(
      options,
      ["expectedPresetRevision", "expectedRevision"],
      "Use local preset options",
    );
    const authorityGeneration = savedLoadGeneration(key);
    const resolved = await localPresetSources.resolve(key, presetId, {
      expectedPresetRevision: parsed.expectedPresetRevision,
    });
    if (resolved.kind !== "ready") {
      const authoritative = await refresh(key, "external");
      return frozenClone({
        ...resolved,
        collectionKey: key,
        boundary: "local-preset-sources",
        operation: "use-local-preset",
        authoritative,
      });
    }
    return replaceCurrent(
      key,
      resolved.preset.payload,
      { kind: "local", id: resolved.preset.id },
      "use-local-preset",
      parsed.expectedRevision,
      authorityGeneration,
    );
  }

  async function useFixed(rawKey, fixedId, options = {}) {
    const key = collectionKey(rawKey);
    const parsed = exactOptions(options, ["expectedRevision"], "Use fixed preset options");
    const idValue = opaqueId(fixedId, "Fixed preset ID");
    const fixed = fixedPresetRowsFor(key).find((row) => row.id === idValue);
    if (!fixed) {
      throw new TypeError(`Unknown fixed preset ${idValue}.`);
    }
    return replaceCurrent(key, fixed.payload, { kind: "fixed", id: fixed.id }, "use-fixed", parsed.expectedRevision);
  }

  async function useRevision(rawKey, revisionId, options = {}) {
    return jumpToRevision(rawKey, revisionId, options);
  }

  async function moveHead(rawKey, cause, selectRevision, options = {}) {
    const key = collectionKey(rawKey);
    const parsed = exactOptions(options, ["expectedRevision", "revisionId"], `${cause} options`);
    const current = await refresh(key, "external");
    const expectedConflict = checkCurrentExpected(current, parsed.expectedRevision);
    if (expectedConflict || current.kind !== "ready") {
      return expectedConflict || current;
    }
    const expectedStoreRevision = current.revision.split("|")[0];
    return mutateWorkspace(key, expectedStoreRevision, (workspace) => {
      const target = selectRevision(workspace, parsed);
      if (target?.kind === "conflict") {
        return target;
      }
      if (!target) {
        return workspace;
      }
      workspace.headRevisionId = target.revisionId;
      workspace.headChangeId = target.changeId;
      return workspace;
    }, cause);
  }

  function jumpToRevision(rawKey, revisionId, options = {}) {
    const idValue = opaqueId(revisionId, "Workspace revision ID");
    return moveHead(rawKey, "jump", (workspace) => {
      const target = workspaceRevisionMap(workspace).get(idValue);
      if (!target || target.changeId !== workspace.headChangeId) {
        throw new TypeError(`Revision ${idValue} is not in Current work.`);
      }
      return target;
    }, options);
  }

  function undo(rawKey, options = {}) {
    return moveHead(rawKey, "undo", (workspace) => {
      const byId = workspaceRevisionMap(workspace);
      const head = byId.get(workspace.headRevisionId);
      if (!head?.parentRevisionId) {
        return null;
      }
      const parent = byId.get(head.parentRevisionId);
      if (!parent || parent.changeId !== workspace.headChangeId) {
        return null;
      }
      setLastVisited(workspace, parent.revisionId, head.revisionId);
      return parent;
    }, options);
  }

  function redo(rawKey, options = {}) {
    return moveHead(rawKey, "redo", (workspace, parsed) => {
      const children = workspaceChildRows(workspace, workspace.headRevisionId);
      if (!children.length) {
        return null;
      }
      if (parsed.revisionId !== undefined) {
        const requested = opaqueId(parsed.revisionId, "Redo revision ID");
        const target = children.find((child) => child.revisionId === requested);
        if (!target) {
          throw new TypeError("Requested redo revision is not a child of Current work.");
        }
        return target;
      }
      const remembered = workspace.lastVisitedChildren
        .find((entry) => entry.parentRevisionId === workspace.headRevisionId);
      if (remembered) {
        return children.find((child) => child.revisionId === remembered.childRevisionId) || children[0];
      }
      if (children.length === 1) {
        return children[0];
      }
      return {
        kind: "conflict",
        collectionKey: workspace.collectionKey,
        reason: "redo-branch-required",
        revisionIds: children.map((child) => child.revisionId),
      };
    }, options);
  }

  function resetCurrent(rawKey, options = {}) {
    return moveHead(rawKey, "reset", (workspace) => {
      const byId = workspaceRevisionMap(workspace);
      const head = byId.get(workspace.headRevisionId);
      if (!head || head.basedOn.kind === "none") {
        return null;
      }
      const base = byId.get(head.basedOn.revisionId) || null;
      if (!base) {
        return null;
      }
      const departedPath = [];
      let child = head;
      while (child.revisionId !== base.revisionId) {
        const parent = child.parentRevisionId ? byId.get(child.parentRevisionId) : null;
        if (!parent || parent.changeId !== workspace.headChangeId) {
          break;
        }
        departedPath.push([parent.revisionId, child.revisionId]);
        child = parent;
      }
      if (child.revisionId === base.revisionId) {
        for (const [parentRevisionId, childRevisionId] of departedPath) {
          setLastVisited(workspace, parentRevisionId, childRevisionId);
        }
      }
      return base;
    }, options);
  }

  async function clearCurrent(rawKey, options = {}) {
    const key = collectionKey(rawKey);
    const parsed = exactOptions(
      options,
      ["expectedRevision", "resetInvalid"],
      "Clear Current options",
    );
    if (parsed.resetInvalid !== undefined && typeof parsed.resetInvalid !== "boolean") {
      throw new TypeError("Clear Current resetInvalid must be a boolean.");
    }
    const current = await refresh(key, "external");
    const expectedConflict = checkCurrentExpected(current, parsed.expectedRevision);
    if (expectedConflict) {
      return expectedConflict;
    }
    if (current.kind === "missing") {
      return current;
    }
    let expectedRaw = null;
    let resetInvalid = false;
    if (current.kind !== "ready") {
      if (
        parsed.resetInvalid !== true
        || (current.kind !== "invalid" && current.kind !== "unsupportedVersion")
      ) {
        return current;
      }
      try {
        expectedRaw = await provider.readWorkspace(key);
      } catch (error) {
        return publish(key, {
          ...providerFailure("read-invalid-preset-current", error),
          collectionKey: key,
        });
      }
      const decoded = decodeWorkspaceDocument(
        expectedRaw,
        contractFor(collectionContracts, key),
        key,
      );
      if (decoded.kind !== "invalid" && decoded.kind !== "unsupportedVersion") {
        return current;
      }
      resetInvalid = true;
    }
    const expectedStoreRevision = current.kind === "ready"
      ? current.revision.split("|")[0]
      : null;
    let result;
    try {
      result = await provider.mutateWorkspace(key, (raw) => {
        if (resetInvalid) {
          if (!rawRecordsEqual(raw, expectedRaw)) {
            return {
              record: raw,
              result: {
                kind: "conflict",
                collectionKey: key,
                reason: "invalid-current-changed",
              },
            };
          }
          return { record: null, result: { kind: "written" } };
        }
        const decoded = decodeWorkspaceDocument(raw, contractFor(collectionContracts, key), key);
        if (decoded.kind !== "ready") {
          return { record: raw, result: decoded };
        }
        if (decoded.storeRevision !== expectedStoreRevision) {
          return {
            record: raw,
            result: {
              kind: "conflict",
              collectionKey: key,
              expectedRevision: expectedStoreRevision,
              actualRevision: decoded.storeRevision,
            },
          };
        }
        return { record: null, result: { kind: "written" } };
      });
    } catch (error) {
      return publish(key, {
        ...providerFailure("clear-preset-current", error),
        collectionKey: key,
      });
    }
    if (result.kind !== "written") {
      const authoritative = await refresh(key, "external");
      return frozenClone({
        ...result,
        collectionKey: key,
        expectedRevision: parsed.expectedRevision ?? current.revision,
        actualRevision: authoritative.revision ?? null,
        authoritative,
      });
    }
    const next = await refresh(key, "external");
    let remainingRaw;
    try {
      remainingRaw = await provider.readWorkspace(key);
    } catch (error) {
      return publish(key, {
        ...providerFailure("verify-cleared-preset-current", error),
        collectionKey: key,
        authoritative: next,
      });
    }
    if (remainingRaw !== null) {
      return publish(key, {
        kind: "unavailable",
        operation: "clear-preset-current",
        reason: "workspace-delete-not-acknowledged",
        collectionKey: key,
        authoritative: next,
      });
    }
    broadcast(key, next.revision);
    return frozenClone({ ...next, effect: { kind: "current-cleared" } });
  }

  async function capturePromotionPayload(key, intent, target, suppliedPayload) {
    if (suppliedPayload !== undefined) {
      return suppliedPayload;
    }
    return runtimeBindings.captureCurrent(
      key,
      { intent, target: target ? cloneValue(target) : null },
    );
  }

  async function preparePromotionPayload(
    key,
    options,
    intent,
    target = null,
    { refreshSaved = false } = {},
  ) {
    let current = await refresh(key, "external", { refreshSaved });
    if (refreshSaved && current.kind === "ready" && !savedReferencesReady(key)) {
      return {
        state: savedReferenceFailure(key, "save-preset", current),
        current: null,
      };
    }
    const expectedConflict = checkCurrentExpected(current, options.expectedRevision);
    if (expectedConflict || current.kind !== "ready") {
      return { state: expectedConflict || current, current: null };
    }
    const captured = await capturePromotionPayload(key, intent, target, options.payload);
    if (captured !== undefined) {
      const contract = contractFor(collectionContracts, key);
      const payload = decodePayload(contract, captured);
      const equalForPromotion = typeof contract.promotionPayloadsEqual === "function"
        ? contract.promotionPayloadsEqual(current.current.payload, payload)
        : payloadsEqual(contract, current.current.payload, payload);
      if (!equalForPromotion) {
        current = await commitCurrentPayload(key, current, payload, {
          actionId: id("promotion_capture_action"),
          actionKind: "promotion-capture",
        });
      }
    }
    return { state: current, current: current.kind === "ready" ? current : null };
  }

  function preparePromotion(key, options, intent, target = null) {
    return preparePromotionPayload(key, options, intent, target, { refreshSaved: true });
  }

  async function attachCurrentToSource(key, source, expectedHeadRevisionId) {
    const canonicalSource = canonicalSourceInput(source);
    let current = await refresh(key, "external");
    if (current.kind !== "ready") {
      return { state: current, attached: false };
    }
    if (current.current.revisionId !== expectedHeadRevisionId) {
      return { state: current, attached: false };
    }
    const expectedStoreRevision = current.revision.split("|")[0];
    const attached = await mutateWorkspace(key, expectedStoreRevision, (workspace) => {
      if (workspace.headRevisionId !== expectedHeadRevisionId) {
        return {
          kind: "conflict",
          collectionKey: key,
          reason: "current-advanced-during-preset-promotion",
        };
      }
      workspaceRevisionMap(workspace).get(workspace.headRevisionId).basedOn = {
        kind: canonicalSource.kind,
        id: canonicalSource.id,
        revisionId: workspace.headRevisionId,
      };
      return workspace;
    }, "external");
    if (attached.kind === "conflict") {
      current = await refresh(key, "external");
      return { state: current, attached: false };
    }
    return { state: attached, attached: attached.kind === "ready" };
  }

  async function saveAs(rawKey, options = {}) {
    const key = collectionKey(rawKey);
    const parsed = exactOptions(options, ["expectedRevision", "name", "payload"], "Save-as options");
    const name = savedPresetName(parsed.name);
    const authorityGeneration = savedLoadGeneration(key);
    const prepared = await preparePromotion(key, parsed, "save");
    if (!prepared.current) {
      return prepared.state;
    }
    const current = prepared.current;
    const savedRevision = current.revision.split("|")[1];
    const authorityFailure = changedSavedAuthority(
      key,
      authorityGeneration,
      "create",
      current.revision,
    );
    if (authorityFailure) return authorityFailure;
    const saved = await savedPresets.create(key, {
      name,
      payload: current.current.payload,
      expectedRevision: savedRevision === "missing" ? null : savedRevision,
    });
    if (saved.kind !== "ready") {
      return savedMutationFailure(key, saved, current.revision);
    }
    rememberSavedState(key, saved);
    const presetId = saved.effect.preset.id;
    const attachment = await attachCurrentToSource(
      key,
      { kind: "preset", id: presetId },
      current.current.revisionId,
    );
    broadcast(key, attachment.state.revision ?? saved.revision, "saved-references");
    return frozenClone({
      ...attachment.state,
      effect: { kind: "preset-created", presetId, attached: attachment.attached },
    });
  }

  async function updatePreset(rawKey, presetId, options = {}) {
    const key = collectionKey(rawKey);
    const parsed = exactOptions(
      options,
      ["expectedPresetRevision", "expectedRevision", "payload"],
      "Update preset options",
    );
    const idValue = opaqueId(presetId, "Saved preset ID");
    const authorityGeneration = savedLoadGeneration(key);
    const prepared = await preparePromotion(key, parsed, "save", { kind: "preset", id: idValue });
    if (!prepared.current) {
      return prepared.state;
    }
    const current = prepared.current;
    const authorityFailure = changedSavedAuthority(
      key,
      authorityGeneration,
      "update",
      current.revision,
    );
    if (authorityFailure) return authorityFailure;
    const saved = await savedPresets.update(key, idValue, {
      payload: current.current.payload,
      expectedRevision: current.revision.split("|")[1],
      expectedPresetRevision: parsed.expectedPresetRevision,
    });
    if (saved.kind !== "ready") {
      return savedMutationFailure(key, saved, current.revision);
    }
    rememberSavedState(key, saved);
    const attachment = await attachCurrentToSource(
      key,
      { kind: "preset", id: idValue },
      current.current.revisionId,
    );
    broadcast(key, attachment.state.revision ?? saved.revision, "saved-references");
    return frozenClone({
      ...attachment.state,
      effect: { kind: "preset-updated", presetId: idValue, attached: attachment.attached },
    });
  }

  async function updateLocalPreset(rawKey, presetId, options = {}) {
    const key = collectionKey(rawKey);
    const parsed = exactOptions(
      options,
      ["expectedPresetRevision", "expectedRevision", "payload"],
      "Update local preset options",
    );
    const idValue = opaqueId(presetId, "Local preset ID");
    const prepared = await preparePromotionPayload(
      key,
      parsed,
      "save",
      { kind: "local", id: idValue },
    );
    if (!prepared.current) {
      return prepared.state;
    }
    const current = prepared.current;
    const updated = await localPresetSources.update(key, idValue, {
      payload: current.current.payload,
      expectedPresetRevision: parsed.expectedPresetRevision,
    });
    if (updated.kind !== "ready") {
      const authoritative = await refresh(key, "external");
      return frozenClone({
        ...updated,
        collectionKey: key,
        boundary: "local-preset-sources",
        operation: "update-local-preset",
        expectedRevision: current.revision,
        actualRevision: authoritative.revision ?? null,
        authoritative,
      });
    }
    const attachment = await attachCurrentToSource(
      key,
      { kind: "local", id: idValue },
      current.current.revisionId,
    );
    broadcast(
      key,
      attachment.state.revision ?? updated.revision,
      "local-preset-sources",
    );
    return frozenClone({
      ...attachment.state,
      effect: { kind: "preset-updated", presetId: idValue, attached: attachment.attached },
    });
  }

  async function renamePreset(rawKey, presetId, options = {}) {
    const key = collectionKey(rawKey);
    const parsed = exactOptions(
      options,
      ["expectedPresetRevision", "expectedRevision", "name"],
      "Rename preset options",
    );
    const authorityGeneration = savedLoadGeneration(key);
    const current = await refresh(key, "external", { refreshSaved: true });
    if (!savedReferencesReady(key)) {
      return savedReferenceFailure(key, "rename-preset", current);
    }
    const expectedConflict = checkCurrentExpected(current, parsed.expectedRevision);
    if (expectedConflict) {
      return expectedConflict;
    }
    const authorityFailure = changedSavedAuthority(
      key,
      authorityGeneration,
      "rename",
      current.revision,
    );
    if (authorityFailure) return authorityFailure;
    const saved = await savedPresets.rename(key, opaqueId(presetId, "Saved preset ID"), {
      name: parsed.name,
      expectedRevision: current.revision?.split("|")[1] === "missing"
        ? null
        : current.revision?.split("|")[1],
      expectedPresetRevision: parsed.expectedPresetRevision,
    });
    if (saved.kind !== "ready") {
      return savedMutationFailure(key, saved, current.revision);
    }
    rememberSavedState(key, saved);
    const next = await refresh(key, "external");
    broadcast(key, next.revision, "saved-references");
    return frozenClone({ ...next, effect: { kind: "preset-renamed", presetId } });
  }

  async function resolveCloneTarget(key, target, expectedPresetRevision = undefined) {
    if (!isRecord(target) || !["preset", "fixed", "local", "revision"].includes(target.kind)) {
      throw new TypeError("Clone target must be a preset, fixed preset, local preset, or revision.");
    }
    if (target.kind === "preset") {
      const saved = currentSavedState(key);
      const idValue = opaqueId(target.id, "Saved preset ID");
      const preset = saved.kind === "ready" ? saved.presets.find((row) => row.id === idValue) : null;
      if (!preset) {
        throw new TypeError(`Unknown saved preset ${idValue}.`);
      }
      if (
        expectedPresetRevision !== undefined
        && preset.revision !== opaqueId(expectedPresetRevision, "Expected preset revision")
      ) {
        return {
          conflict: {
            kind: "conflict",
            collectionKey: key,
            expectedPresetRevision,
            actualPresetRevision: preset.revision,
          },
        };
      }
      return { payload: preset.payload, defaultName: `${preset.name} copy` };
    }
    if (target.kind === "local") {
      const resolved = await localPresetSources.resolve(key, target.id, {
        expectedPresetRevision,
      });
      if (resolved.kind === "conflict") {
        return { conflict: resolved };
      }
      if (resolved.kind !== "ready") {
        return { failure: resolved };
      }
      return {
        payload: resolved.preset.payload,
        defaultName: resolved.preset.name,
      };
    }
    if (target.kind === "fixed") {
      const idValue = opaqueId(target.id, "Fixed preset ID");
      const fixed = fixedPresetRowsFor(key).find((row) => row.id === idValue);
      if (!fixed) {
        throw new TypeError(`Unknown fixed preset ${idValue}.`);
      }
      return { payload: fixed.payload, defaultName: `${fixed.name} copy` };
    }
    const revisionId = opaqueId(target.revisionId, "Workspace revision ID");
    const raw = await provider.readWorkspace(key);
    const workspace = decodeWorkspaceDocument(raw, contractFor(collectionContracts, key), key);
    const revision = workspace.kind === "ready"
      ? workspaceRevisionMap(workspace).get(revisionId)
      : null;
    if (!revision) {
      throw new TypeError(`Unknown workspace revision ${revisionId}.`);
    }
    return { payload: revision.payload, defaultName: "Preset copy" };
  }

  async function clonePreset(rawKey, target, options = {}) {
    const key = collectionKey(rawKey);
    const parsed = exactOptions(
      options,
      ["expectedPresetRevision", "expectedRevision", "name", "payload"],
      "Clone preset options",
    );
    const authorityGeneration = savedLoadGeneration(key);
    let current = await refresh(key, "external", { refreshSaved: true });
    if (!savedReferencesReady(key)) {
      return savedReferenceFailure(key, "clone-preset", current);
    }
    const expectedConflict = checkCurrentExpected(current, parsed.expectedRevision);
    if (expectedConflict) {
      return expectedConflict;
    }
    const resolved = await resolveCloneTarget(key, target, parsed.expectedPresetRevision);
    if (resolved.conflict) {
      return frozenClone(resolved.conflict);
    }
    if (resolved.failure) {
      return frozenClone({
        ...resolved.failure,
        boundary: "local-preset-sources",
        operation: "clone-local-preset",
        authoritative: current,
      });
    }
    const targetIsCurrent = current.kind === "ready" && (
      (target.kind === "revision" && target.revisionId === current.current.revisionId)
      || (
        (target.kind === "preset" || target.kind === "fixed")
        && current.current.basedOn.kind === target.kind
        && current.current.basedOn.id === target.id
      )
    );
    const captured = targetIsCurrent
      ? await capturePromotionPayload(key, "clone", target, parsed.payload)
      : undefined;
    let payload = resolved.payload;
    if (captured !== undefined) {
      payload = decodePayload(contractFor(collectionContracts, key), captured);
      if (current.kind === "ready" && !payloadsEqual(
        contractFor(collectionContracts, key),
        current.current.payload,
        payload,
      )) {
        current = await edit(key, payload, {
          expectedRevision: current.revision,
          actionId: id("promotion_capture_action"),
          actionKind: "promotion-capture",
        });
      }
    }
    if (current.kind !== "ready" && current.kind !== "missing") {
      return current;
    }
    const expectedSaved = current.revision?.split("|")[1];
    const authorityFailure = changedSavedAuthority(
      key,
      authorityGeneration,
      "create",
      current.revision,
    );
    if (authorityFailure) return authorityFailure;
    const saved = await savedPresets.create(key, {
      name: parsed.name === undefined ? resolved.defaultName : parsed.name,
      payload,
      expectedRevision: expectedSaved === "missing" ? null : expectedSaved,
    });
    if (saved.kind !== "ready") {
      return savedMutationFailure(key, saved, current.revision);
    }
    rememberSavedState(key, saved);
    const next = await refresh(key, "external");
    broadcast(key, next.revision, "saved-references");
    return frozenClone({ ...next, effect: { kind: "preset-created", presetId: saved.effect.preset.id } });
  }

  async function deletePreset(rawKey, presetId, options = {}) {
    const key = collectionKey(rawKey);
    const parsed = exactOptions(
      options,
      ["expectedPresetRevision", "expectedRevision"],
      "Delete preset options",
    );
    const authorityGeneration = savedLoadGeneration(key);
    let current = await refresh(key, "external", { refreshSaved: true });
    if (!savedReferencesReady(key)) {
      return savedReferenceFailure(key, "delete-preset", current);
    }
    const expectedConflict = checkCurrentExpected(current, parsed.expectedRevision);
    if (expectedConflict) {
      return expectedConflict;
    }
    const idValue = opaqueId(presetId, "Saved preset ID");
    const authorityFailure = changedSavedAuthority(
      key,
      authorityGeneration,
      "remove",
      current.revision,
    );
    if (authorityFailure) return authorityFailure;
    const saved = await savedPresets.remove(key, idValue, {
      expectedRevision: current.revision?.split("|")[1] === "missing"
        ? null
        : current.revision?.split("|")[1],
      expectedPresetRevision: parsed.expectedPresetRevision,
    });
    if (saved.kind !== "ready") {
      return savedMutationFailure(key, saved, current.revision);
    }
    rememberSavedState(key, saved);
    current = await refresh(key, "external");
    if (current.kind === "ready" && current.current.basedOn.kind === "preset" && current.current.basedOn.id === idValue) {
      const expectedStoreRevision = current.revision.split("|")[0];
      current = await mutateWorkspace(key, expectedStoreRevision, (workspace) => {
        for (const revision of workspace.revisions) {
          if (revision.basedOn.kind === "preset" && revision.basedOn.id === idValue) {
            revision.basedOn = detachedSource();
          }
        }
        return workspace;
      }, "external");
    }
    broadcast(key, current.revision, "saved-references");
    return frozenClone({ ...current, effect: { kind: "preset-deleted", presetId: idValue } });
  }

  function collectionInfo(rawKey) {
    const key = collectionKey(rawKey);
    const contract = contractFor(collectionContracts, key);
    return frozenClone(contract.info || {});
  }

  function fixedPresets(rawKey) {
    const key = collectionKey(rawKey);
    return frozenClone(fixedPresetRowsFor(key));
  }

  async function defineFixedPreset(rawKey, definition) {
    const key = collectionKey(rawKey);
    const contract = contractFor(collectionContracts, key);
    if (!exactFields(definition, FIXED_PRESET_DEFINITION_FIELDS)) {
      throw new TypeError("Fixed preset definition must contain exactly id, name, and payload.");
    }
    const [row] = fixedRows({ ...contract, fixedPresets: [definition] });
    const declared = runtimeFixedPresetDescriptors(contract)
      .some((descriptor) => descriptor.id === row.id && descriptor.name === row.name);
    if (!declared) {
      throw new TypeError(`Runtime fixed preset ${row.id} is not declared for ${key}.`);
    }
    const definitions = definedFixedPresets.get(key) || new Map();
    const existing = definitions.get(row.id);
    if (
      existing
      && existing.name === row.name
      && payloadsEqual(contract, existing.payload, row.payload)
    ) {
      return frozenClone(existing);
    }
    definitions.set(row.id, frozenClone(row));
    definedFixedPresets.set(key, definitions);
    if (states.has(key) && !disposed) {
      await reprojectLocally(key);
    }
    return frozenClone(row);
  }

  function bindCollectionRuntime(rawKey, binding) {
    const key = collectionKey(rawKey);
    contractFor(collectionContracts, key);
    return runtimeBindings.bind(key, binding, states.get(key));
  }

  async function exportCollection(rawKey, options = {}) {
    const key = collectionKey(rawKey);
    const parsed = exactOptions(
      options,
      ["currentName", "includeCurrent", "presetIds"],
      "Preset export options",
    );
    const current = await refresh(key, "external", { refreshSaved: true });
    if (current.kind !== "ready") {
      throw new TypeError("Preset export requires a ready authoritative workspace.");
    }
    if (!savedReferencesReady(key)) {
      throw new TypeError("Saved preset references are unavailable.");
    }
    if (parsed.presetIds !== undefined && !Array.isArray(parsed.presetIds)) {
      throw new TypeError("Preset export IDs must be an array.");
    }
    const ids = parsed.presetIds === undefined
      ? null
      : new Set(parsed.presetIds.map((value) => opaqueId(value, "Saved preset ID")));
    if (ids) {
      const availableIds = new Set(current.presets.map((preset) => preset.id));
      const missingId = Array.from(ids).find((presetId) => !availableIds.has(presetId));
      if (missingId) {
        throw new TypeError(`Unknown saved preset ${missingId}.`);
      }
    }
    const sourcePresets = (current.presets || []).filter((preset) => !ids || ids.has(preset.id));
    const presets = sourcePresets.map((preset) => ({
      id: preset.id,
      name: preset.name,
      payload: cloneValue(preset.payload),
      createdAt: preset.createdAt,
      updatedAt: preset.updatedAt,
    }));
    if (!presets.length && parsed.includeCurrent === true && current.kind === "ready") {
      const at = timestamp();
      presets.push({
        id: id("preset_export"),
        name: parsed.currentName === undefined
          ? "Preset 1"
          : savedPresetName(parsed.currentName, "Exported preset name"),
        payload: cloneValue(current.current.payload),
        createdAt: at,
        updatedAt: at,
      });
    }
    const selectedPresetId = current.kind === "ready" && current.current.basedOn.kind === "preset"
      && presets.some((preset) => preset.id === current.current.basedOn.id)
      ? current.current.basedOn.id
      : "";
    return frozenClone({
      format: PRESET_INTERCHANGE_FORMAT,
      collectionKey: key,
      exportedAt: timestamp(),
      selectedPresetId,
      presets,
    });
  }

  async function exportLocalPreset(rawKey, presetId, options = {}) {
    const key = collectionKey(rawKey);
    const parsed = exactOptions(
      options,
      ["expectedPresetRevision"],
      "Local preset export options",
    );
    const resolved = await localPresetSources.resolve(key, presetId, {
      expectedPresetRevision: parsed.expectedPresetRevision,
    });
    if (resolved.kind !== "ready") {
      throw new TypeError(
        resolved.kind === "conflict"
          ? "The local preset changed before it could be exported."
          : "Browser-local presets are unavailable.",
      );
    }
    const preset = resolved.preset;
    return frozenClone({
      format: PRESET_INTERCHANGE_FORMAT,
      collectionKey: key,
      exportedAt: timestamp(),
      selectedPresetId: preset.id,
      presets: [{
        id: preset.id,
        name: preset.name,
        payload: cloneValue(preset.payload),
        createdAt: preset.createdAt,
        updatedAt: preset.updatedAt,
      }],
    });
  }

  async function exportRawCollection(rawKey) {
    const key = collectionKey(rawKey);
    contractFor(collectionContracts, key);
    try {
      const [current, saved, quarantine] = await Promise.all([
        provider.readWorkspace(key),
        provider.readSaved(key),
        provider.quarantineForCollection(key),
      ]);
      return frozenClone({
        kind: "ready",
        collectionKey: key,
        current,
        saved,
        quarantine,
      });
    } catch (error) {
      return frozenClone({
        ...providerFailure("export-raw-preset-collection", error),
        collectionKey: key,
      });
    }
  }

  async function importCollection(rawKey, interchange, options = {}) {
    const key = collectionKey(rawKey);
    const parsed = exactOptions(options, ["expectedRevision"], "Preset import options");
    const authorityGeneration = savedLoadGeneration(key);
    const current = await refresh(key, "external", { refreshSaved: true });
    if (!savedReferencesReady(key)) {
      return savedReferenceFailure(key, "import-presets", current);
    }
    const expectedConflict = checkCurrentExpected(current, parsed.expectedRevision);
    if (expectedConflict) {
      return expectedConflict;
    }
    const decoded = decodePresetInterchange(interchange, contractFor(collectionContracts, key), key);
    const savedRevision = current.revision?.split("|")[1];
    if (typeof savedPresets.importMany !== "function") {
      throw new TypeError("Saved-preset provider does not support atomic import.");
    }
    const authorityFailure = changedSavedAuthority(
      key,
      authorityGeneration,
      "importMany",
      current.revision,
    );
    if (authorityFailure) return authorityFailure;
    const imported = await savedPresets.importMany(key, decoded.presets, {
      expectedRevision: savedRevision === "missing" ? null : savedRevision,
    });
    if (imported.kind !== "ready") {
      return savedMutationFailure(key, imported, current.revision);
    }
    rememberSavedState(key, imported);
    const importedIds = imported.effect.presetIds;
    const next = await refresh(key, "external");
    broadcast(key, next.revision, "saved-references");
    return frozenClone({ ...next, effect: { kind: "presets-imported", presetIds: importedIds } });
  }

  async function resetAll() {
    try {
      await provider.clearAll();
    } catch (error) {
      const failure = frozenClone(providerFailure("reset-all-presets", error));
      for (const key of keys()) {
        publish(key, { ...failure, collectionKey: key });
      }
      return failure;
    }
    const result = {};
    for (const key of keys()) {
      const next = await refresh(key, "external", { refreshSaved: true });
      result[key] = next;
      broadcast(key, next.revision, "saved-references");
    }
    return frozenClone(result);
  }

  function handleBroadcast(event) {
    if (disposed || !isRecord(event?.data)) {
      return;
    }
    let key;
    try {
      key = collectionKey(event.data.collectionKey);
      contractFor(collectionContracts, key);
    } catch (_error) {
      return;
    }
    if (event.data.boundary === "local-preset-sources") {
      localPresetSources.invalidate(key);
      void localPresetSources.load(key).then(() => refresh(key, "external"));
      return;
    }
    if (event.data.storeRevision === state(key).revision) {
      return;
    }
    void refresh(key, "external", {
      refreshSaved: event.data.boundary === "saved-references",
    });
  }

  function handlePageShow(event) {
    if (disposed || event?.persisted !== true) {
      return;
    }
    const loadedKeys = Array.from(states.keys());
    for (const key of loadedKeys) {
      obsoleteSavedLoad(key);
      hideSavedReferences(key);
    }
    void (async () => {
      try {
        await savedPresets.refreshAuthority?.();
      } catch (_error) {
        // The selected saved-reference provider projects its own typed failure.
      }
      if (disposed) return;
      await Promise.all(loadedKeys.map((key) => (
        refresh(key, "external", { refreshSaved: true })
      )));
    })();
  }

  function handleSavedAuthorityChange() {
    if (disposed) return;
    const affectedKeys = new Set([
      ...states.keys(),
      ...savedStates.keys(),
      ...savedLoads.keys(),
    ]);
    for (const key of affectedKeys) {
      obsoleteSavedLoad(key);
      hideSavedReferences(key);
      void refresh(key, "external", { refreshSaved: true });
    }
  }

  broadcaster?.addEventListener?.("message", handleBroadcast);
  eventTarget?.addEventListener?.("pageshow", handlePageShow);
  const unsubscribeSavedAuthority = typeof savedPresets.subscribeAuthority === "function"
    ? savedPresets.subscribeAuthority(handleSavedAuthorityChange)
    : null;
  if (unsubscribeSavedAuthority !== null && typeof unsubscribeSavedAuthority !== "function") {
    throw new TypeError("Saved-preset authority subscription must return an unsubscribe function.");
  }

  async function dispose() {
    if (disposed) {
      return;
    }
    disposed = true;
    unsubscribeSavedAuthority?.();
    broadcaster?.removeEventListener?.("message", handleBroadcast);
    eventTarget?.removeEventListener?.("pageshow", handlePageShow);
    broadcaster?.close?.();
    listeners.clear();
    runtimeBindings.dispose();
    definedFixedPresets.clear();
    savedStates.clear();
    localPresetSources.clear();
    for (const [key, controller] of projections) {
      settleProjectionWaiters(controller, state(key));
    }
    await savedPresets.dispose?.();
    await provider.dispose?.();
  }

  return Object.freeze({
    load,
    state,
    subscribe,
    initialize,
    edit,
    usePreset,
    useLocalPreset,
    useFixed,
    useRevision,
    jumpToRevision,
    undo,
    redo,
    saveAs,
    updatePreset,
    updateLocalPreset,
    renamePreset,
    clonePreset,
    deletePreset,
    resetCurrent,
    clearCurrent,
    resetAll,
    importCollection,
    exportCollection,
    exportLocalPreset,
    exportRawCollection,
    collectionInfo,
    fixedPresets,
    defineFixedPreset,
    bindCollectionRuntime,
    dispose,
  });
}
